import { useEffect, useRef, useCallback } from 'react';
import { safeGetJSON, safeGetArray } from '@/lib/safeStorage';

interface Milestone {
  id: string;
  title: string;
  description: string;
  achievementId?: string;
  threshold: number;
  type: 'rooms' | 'streak' | 'fragments';
}

const MILESTONES: Milestone[] = [
  { id: 'first-room', title: 'First Door Opened', description: 'You took the first step into the unknown.', achievementId: 'first-step', threshold: 1, type: 'rooms' },
  { id: '10-rooms', title: 'Ten Doors Deep', description: 'The hallway remembers you now.', achievementId: 'explorer', threshold: 10, type: 'rooms' },
  { id: '25-rooms', title: 'A Quarter Century', description: 'Each room left a mark. You carry them all.', achievementId: 'wanderer', threshold: 25, type: 'rooms' },
  { id: '50-rooms', title: 'Half the Map', description: 'You have explored more than most ever will.', achievementId: 'cartographer', threshold: 50, type: 'rooms' },
  { id: '100-rooms', title: 'The Centurion', description: 'A hundred doors, a hundred truths. You are not the same person who entered.', achievementId: 'hundred-rooms', threshold: 100, type: 'rooms' },
  { id: '7-streak', title: 'Seven Days', description: 'A week of returning. This is becoming ritual.', achievementId: 'devoted', threshold: 7, type: 'streak' },
  { id: '30-streak', title: 'Thirty Days', description: 'A month. The rooms have become part of you.', achievementId: 'eternal', threshold: 30, type: 'streak' },
];

const SEEN_KEY = 'milestones-seen';

export const useMilestones = () => {
  const pendingRef = useRef<Milestone | null>(null);
  const callbackRef = useRef<((m: Milestone) => void) | null>(null);

  const checkMilestones = useCallback(() => {
    const progress = safeGetJSON<{ completedRooms?: string[] }>('human-rooms-progress', { completedRooms: [] });
    const roomCount = progress.completedRooms?.length || 0;
    const seen: string[] = safeGetArray<string>(SEEN_KEY);

    for (const milestone of MILESTONES) {
      if (seen.includes(milestone.id)) continue;
      
      let reached = false;
      if (milestone.type === 'rooms') {
        reached = roomCount >= milestone.threshold;
      }
      // Could add streak/fragment checks here
      
      if (reached) {
        // Mark as seen
        try {
          localStorage.setItem(SEEN_KEY, JSON.stringify([...seen, milestone.id]));
        } catch {}
        
        pendingRef.current = milestone;
        callbackRef.current?.(milestone);
        return milestone;
      }
    }
    return null;
  }, []);

  const onMilestone = useCallback((cb: (m: Milestone) => void) => {
    callbackRef.current = cb;
  }, []);

  return { checkMilestones, onMilestone, MILESTONES };
};
